// src/components/common/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);

  // Detectar scroll para cambiar el estilo de la barra de navegación
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Cerrar el menú cuando la pantalla se redimensiona a un tamaño mayor
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024 && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  // Evitar scroll cuando el menú está abierto en móvil
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Variantes de animación para el menú móvil
  const menuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.2,
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    },
    open: {
      opacity: 1,
      height: "100vh",
      transition: {
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1,
        staggerDirection: 1
      }
    }
  };

  const itemVariants = {
    closed: {
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 }
    },
    open: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  // Lista de enlaces de navegación.
  // Items con `children` muestran submenu (dropdown en desktop, acordeón en mobile).
  const navLinks = [
    { name: 'Institución', path: '/about' },
    { name: 'Universidad', path: '/university' },
    {
      name: 'Oportunidades',
      path: '/opportunities',
      children: [
        {
          name: 'Bolsa de empleos',
          path: '/opportunities',
          description: 'Ofertas laborales en audio, video, iluminación y escenarios.'
        },
        {
          name: 'Para empresas',
          path: '/empresas',
          description: 'Buscá profesionales AV, publicá empleos y construí tu sitio web empresarial.'
        },
        {
          name: 'Para profesionales',
          path: '/profesionales',
          description: 'Empleos, cursos, certificaciones y comunidad para profesionales AV.'
        }
      ]
    },
    { name: 'Calendario', path: '/calendar' },
    {
      name: 'Noticias',
      path: '/news',
      children: [
        {
          name: 'Noticias del sector',
          path: '/news',
          description: 'Actualidad y novedades del sector audiovisual en Latinoamérica.'
        },
        {
          name: 'AVlink Espectro',
          path: '/espectro',
          description: 'Semanario editorial de la industria AV. Cada martes una nueva edición.'
        }
      ]
    },
    { name: 'Sponsors', path: '/sponsors' },
    { name: 'Contacto', path: '/contact' }
  ];

  // URL del logo
  const logoUrl = "https://izghgwyancrwsuxajfah.supabase.co/storage/v1/object/sign/images/imagenes/LogoFondoOsc.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJpbWFnZXMvaW1hZ2VuZXMvTG9nb0ZvbmRvT3NjLnBuZyIsImlhdCI6MTc0Njc5NDQyMSwiZXhwIjoyMDYyMTU0NDIxfQ.PkGfUnDqGlufzBnCcGI5-RSonF8jBwXpj1ZSy4xXfIQ";

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isOpen ? 'bg-dark-900' : scrolled ? 'bg-dark-800/95 backdrop-blur-sm shadow-lg' : 'bg-dark-900/70'}`}>
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo con imagen y texto */}
          <Link to="/" className="relative z-50 flex items-center">
            <img 
              src={logoUrl} 
              alt="Avlink Network Logo" 
              className="h-10 sm:h-12 lg:h-14 mr-2 sm:mr-3" 
            />
            <span className="text-xl sm:text-2xl font-bold text-white">
              <span className="text-primary-500">AVLink</span>{" "}
              <span className="sm:inline">Network</span>
            </span>
          </Link>

          {/* Botón de menú móvil */}
          <button
            className="lg:hidden relative z-50 p-1 rounded-md focus:outline-none"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Cerrar menú" : "Abrir menú"}
          >
            {isOpen ? (
              <FiX className="h-6 w-6 text-white" />
            ) : (
              <FiMenu className="h-6 w-6 text-white" />
            )}
          </button>

          {/* Enlaces de navegación - Escritorio */}
          <div className="hidden lg:flex lg:flex-grow lg:items-center">
            {/* Enlaces centrados */}
            <div className="flex mx-auto justify-center space-x-4 xl:space-x-6">
              {navLinks.map((link) => {
                if (link.children) {
                  return (
                    <div
                      key={link.name}
                      className="relative"
                      onMouseEnter={() => setOpenSubmenu(link.name)}
                      onMouseLeave={() => setOpenSubmenu(null)}
                    >
                      <Link
                        to={link.path}
                        className="text-gray-300 hover:text-primary-400 transition-colors duration-200 font-medium text-sm xl:text-base whitespace-nowrap inline-flex items-center"
                      >
                        {link.name}
                        <FiChevronDown className={`ml-1 transition-transform duration-200 ${openSubmenu === link.name ? 'rotate-180' : ''}`} />
                      </Link>

                      <AnimatePresence>
                        {openSubmenu === link.name && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.15 }}
                            className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
                          >
                            <div className="w-80 bg-dark-800 border border-dark-700 rounded-xl shadow-2xl overflow-hidden">
                              {link.children.map((child) => (
                                <Link
                                  key={child.path}
                                  to={child.path}
                                  className="block px-5 py-4 hover:bg-dark-700 transition-colors border-b border-dark-700 last:border-b-0"
                                  onClick={() => setOpenSubmenu(null)}
                                >
                                  <div className="text-white font-medium mb-1">{child.name}</div>
                                  <div className="text-gray-400 text-xs leading-snug">{child.description}</div>
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-200 font-medium text-sm xl:text-base whitespace-nowrap"
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            {/* Botones de acción a la derecha */}
            <div className="flex items-center space-x-2 xl:space-x-3 ml-auto">
              <Link
                to="/login"
                className="text-white border border-gray-600 hover:border-primary-400 py-1.5 px-3 xl:py-2 xl:px-4 rounded transition-colors font-medium text-sm whitespace-nowrap"
              >
                Login
              </Link>
              <Link
                to="Register"
                className="bg-primary-600 hover:bg-primary-500 text-white py-1.5 px-3 xl:py-2 xl:px-5 rounded-lg transition-colors duration-200 shadow-glow font-medium text-sm whitespace-nowrap"
              >
                Únete
              </Link>
            </div>
          </div>
        </div>

        {/* Menú móvil */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              className="lg:hidden fixed inset-0 top-0 bg-dark-900/95 backdrop-blur-md z-40 flex flex-col items-center justify-center overflow-hidden"
            >
              <div className="flex flex-col items-center justify-center w-full h-full overflow-auto py-16">
                {/* Enlaces principales */}
                <div className="flex flex-col items-center space-y-4 sm:space-y-5 mb-8 sm:mb-12 w-full px-6">
                  {navLinks.map((link) => {
                    if (link.children) {
                      const expanded = mobileExpanded === link.name;
                      return (
                        <motion.div key={link.name} variants={itemVariants} className="w-full max-w-xs">
                          <button
                            type="button"
                            onClick={() => setMobileExpanded(expanded ? null : link.name)}
                            className="w-full flex items-center justify-center gap-2 text-lg sm:text-xl text-white hover:text-primary-400 transition-colors duration-200 font-medium"
                          >
                            {link.name}
                            <FiChevronDown className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                          </button>
                          <AnimatePresence>
                            {expanded && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden mt-3 space-y-2"
                              >
                                {link.children.map((child) => (
                                  <Link
                                    key={child.path}
                                    to={child.path}
                                    className="block text-center text-base text-gray-300 hover:text-primary-400 py-2 px-4 bg-dark-800 rounded-lg transition-colors"
                                    onClick={() => {
                                      setIsOpen(false);
                                      setMobileExpanded(null);
                                    }}
                                  >
                                    {child.name}
                                  </Link>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    }
                    return (
                      <motion.div key={link.name} variants={itemVariants}>
                        <Link
                          to={link.path}
                          className="text-lg sm:text-xl text-white hover:text-primary-400 transition-colors duration-200 font-medium"
                          onClick={() => setIsOpen(false)}
                        >
                          {link.name}
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Botones de acción */}
                <div className="flex space-x-4 sm:flex-col sm:space-x-0 sm:space-y-6 items-center">
                  <motion.div variants={itemVariants}>
                    <Link
                      to="/login"
                      className="text-white border border-gray-600 hover:border-primary-400 py-2 px-5 sm:px-6 rounded transition-colors font-medium"
                      onClick={() => setIsOpen(false)}
                    >
                      Login
                    </Link>
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <Link
                      to="/Register"
                      className="bg-primary-600 hover:bg-primary-500 text-white py-2 px-5 sm:py-3 sm:px-7 rounded-lg transition-colors duration-200 shadow-glow font-medium sm:text-lg"
                      onClick={() => setIsOpen(false)}
                    >
                      Únete
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Navbar;